import React, { useState, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '@/utils';

export default function IssuesByHierarchyCard({ issues, missions, objectives, activities }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('missions');
  const [showFullDialog, setShowFullDialog] = useState(false);
  const [dialogTab, setDialogTab] = useState('missions');

  // Only count open/under_review issues
  const openIssues = useMemo(() => {
    return issues.filter(i => i.status === 'open' || i.status === 'under_review');
  }, [issues]);

  // Count issues by mission
  const issuesByMission = useMemo(() => {
    const counts = {};
    openIssues.forEach(issue => {
      let missionId = issue.linked_mission_id;
      
      // If no direct mission link, try to derive from objective or activity
      if (!missionId && issue.linked_objective_id) {
        const obj = objectives.find(o => o.id === issue.linked_objective_id);
        if (obj) missionId = obj.mission_id;
      }
      if (!missionId && issue.linked_activity_id) {
        const act = activities.find(a => a.id === issue.linked_activity_id);
        if (act) {
          const obj = objectives.find(o => o.id === act.objective_id);
          if (obj) missionId = obj.mission_id;
        }
      }
      
      if (missionId) {
        counts[missionId] = (counts[missionId] || 0) + 1;
      }
    });
    
    return Object.entries(counts)
      .map(([id, count]) => {
        const mission = missions.find(m => m.id === id);
        return {
          id,
          code: mission?.mission_code || 'Unknown',
          name: mission?.title || 'Unknown Mission',
          count
        };
      })
      .sort((a, b) => b.count - a.count);
  }, [openIssues, missions, objectives, activities]);

  // Count issues by objective
  const issuesByObjective = useMemo(() => {
    const counts = {};
    openIssues.forEach(issue => {
      let objectiveId = issue.linked_objective_id;
      
      // If no direct objective link, try to derive from activity
      if (!objectiveId && issue.linked_activity_id) {
        const act = activities.find(a => a.id === issue.linked_activity_id);
        if (act) objectiveId = act.objective_id;
      }
      
      if (objectiveId) {
        counts[objectiveId] = (counts[objectiveId] || 0) + 1;
      }
    });
    
    return Object.entries(counts)
      .map(([id, count]) => {
        const objective = objectives.find(o => o.id === id);
        return {
          id,
          code: objective?.objective_code || 'Unknown',
          name: objective?.statement || 'Unknown Objective',
          missionId: objective?.mission_id,
          count
        };
      })
      .sort((a, b) => b.count - a.count);
  }, [openIssues, objectives, activities]);

  // Count issues by activity
  const issuesByActivity = useMemo(() => {
    const counts = {};
    openIssues.forEach(issue => {
      if (issue.linked_activity_id) {
        counts[issue.linked_activity_id] = (counts[issue.linked_activity_id] || 0) + 1;
      }
    });
    
    return Object.entries(counts)
      .map(([id, count]) => {
        const activity = activities.find(a => a.id === id);
        const objective = activity ? objectives.find(o => o.id === activity.objective_id) : null;
        return {
          id,
          code: activity?.activity_code || 'Unknown',
          name: activity?.title || 'Unknown Activity',
          objectiveId: activity?.objective_id,
          missionId: objective?.mission_id,
          count
        };
      })
      .sort((a, b) => b.count - a.count);
  }, [openIssues, activities, objectives]);

  const getDataForTab = (tab) => {
    switch (tab) {
      case 'missions': return issuesByMission;
      case 'objectives': return issuesByObjective;
      case 'activities': return issuesByActivity;
      default: return [];
    }
  };

  const handleBarClick = (item, tab) => {
    // Navigate to map view with the code as search param
    navigate(createPageUrl('civilizationmap') + `?search=${item.code}`);
  };

  const renderBars = (data, tab, limit = 10) => {
    if (data.length === 0) {
      return (
        <div className="text-center py-4 text-slate-500 text-sm">
          No issues linked to {tab}
        </div>
      );
    }

    const top10 = data.slice(0, limit);
    const others = data.slice(limit);
    const otherCount = others.reduce((sum, item) => sum + item.count, 0);
    const maxCount = Math.max(...data.map(d => d.count));

    return (
      <div className="space-y-2">
        {top10.map((item, index) => (
          <div 
            key={item.id} 
            className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => handleBarClick(item, tab)}
          >
            <div className="w-20 text-xs text-slate-600 truncate font-mono" title={item.code}>
              {item.code}
            </div>
            <div className="flex-1 relative">
              <div className="h-6 bg-slate-200 rounded overflow-hidden">
                <div
                  className="h-full bg-rose-500 flex items-center justify-center text-white font-bold text-xs transition-all hover:bg-rose-600"
                  style={{ width: `${(item.count / maxCount) * 100}%`, minWidth: '24px' }}
                >
                  {item.count}
                </div>
              </div>
            </div>
          </div>
        ))}
        
        {others.length > 0 && (
          <div 
            className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => {
              setDialogTab(tab);
              setShowFullDialog(true);
            }}
          >
            <div className="w-20 text-xs text-slate-600">Other</div>
            <div className="flex-1 relative">
              <div className="h-6 bg-slate-200 rounded overflow-hidden">
                <div
                  className="h-full bg-slate-400 flex items-center justify-center text-white font-bold text-xs transition-all hover:bg-slate-500"
                  style={{ width: `${(otherCount / maxCount) * 100}%`, minWidth: '24px' }}
                >
                  {otherCount}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Card className="glass-panel border-none overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle className="text-slate-700 text-lg font-semibold tracking-tight flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-rose-400" />
            Issues by Missions/Objectives/Activities
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4 bg-slate-100">
              <TabsTrigger value="missions" className="text-xs">
                Missions ({issuesByMission.length})
              </TabsTrigger>
              <TabsTrigger value="objectives" className="text-xs">
                Objectives ({issuesByObjective.length})
              </TabsTrigger>
              <TabsTrigger value="activities" className="text-xs">
                Activities ({issuesByActivity.length})
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="missions" className="mt-0">
              {renderBars(issuesByMission, 'missions')}
            </TabsContent>
            <TabsContent value="objectives" className="mt-0">
              {renderBars(issuesByObjective, 'objectives')}
            </TabsContent>
            <TabsContent value="activities" className="mt-0">
              {renderBars(issuesByActivity, 'activities')}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Full List Dialog */}
      <Dialog open={showFullDialog} onOpenChange={setShowFullDialog}>
        <DialogContent data-testid="dash-issuesbyhierarchycard-dialog" className="bg-white border-slate-200 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-rose-400" />
              All Issues by {dialogTab.charAt(0).toUpperCase() + dialogTab.slice(1)}
            </DialogTitle>
          </DialogHeader>
          
          <Tabs value={dialogTab} onValueChange={setDialogTab}>
            <TabsList className="mb-4 bg-slate-100">
              <TabsTrigger value="missions">Missions</TabsTrigger>
              <TabsTrigger value="objectives">Objectives</TabsTrigger>
              <TabsTrigger value="activities">Activities</TabsTrigger>
            </TabsList>
            
            <TabsContent value="missions">
              {renderFullList(issuesByMission, 'missions', handleBarClick)}
            </TabsContent>
            <TabsContent value="objectives">
              {renderFullList(issuesByObjective, 'objectives', handleBarClick)}
            </TabsContent>
            <TabsContent value="activities">
              {renderFullList(issuesByActivity, 'activities', handleBarClick)}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  );
}

function renderFullList(data, tab, onBarClick) {
  if (data.length === 0) {
    return (
      <div className="text-center py-8 text-slate-500 text-sm">
        No issues linked to {tab}
      </div>
    );
  }

  const maxCount = Math.max(...data.map(d => d.count));

  return (
    <div className="space-y-2">
      {data.map((item) => (
        <div 
          key={item.id} 
          className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity p-2 rounded hover:bg-slate-50"
          onClick={() => onBarClick(item, tab)}
        >
          <div className="w-24 flex-shrink-0">
            <div className="text-xs font-mono text-indigo-600 font-semibold">{item.code}</div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-slate-700 truncate" title={item.name}>{item.name}</div>
          </div>
          <div className="w-32 flex-shrink-0">
            <div className="h-5 bg-slate-200 rounded overflow-hidden">
              <div
                className="h-full bg-rose-500 flex items-center justify-center text-white font-bold text-xs"
                style={{ width: `${(item.count / maxCount) * 100}%`, minWidth: '20px' }}
              >
                {item.count}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}